import React from "react";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Edit2, Trash2 } from "lucide-react";
import { StaffShift } from "@/models/attendanceModel";
import { useTranslation } from "react-i18next";

interface ShiftScheduleTableProps {
  shifts: StaffShift[];
  onEdit: (shift: StaffShift) => void;
  onDelete: (shiftId: string) => void;
}

const daysOfWeek = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

// Format time (e.g. 09:00 -> 9:00 AM)
const formatTime = (time: string) => {
  const [hours, minutes] = time.split(":");
  const hour = parseInt(hours, 10);
  return `${hour % 12 || 12}:${minutes} ${hour >= 12 ? "PM" : "AM"}`;
};

export const ShiftScheduleTable = ({
  shifts,
  onEdit,
  onDelete,
}: ShiftScheduleTableProps) => {
  const { t } = useTranslation();

  // Sort shifts by day of week
  const sortedShifts = [...shifts].sort(
    (a, b) => a.day_of_week - b.day_of_week
  );

  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("day")}</TableHead>
            <TableHead>{t("startTime")}</TableHead>
            <TableHead>{t("endTime")}</TableHead>
            <TableHead>{t("duration")}</TableHead>
            <TableHead className="text-right">{t("actions")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedShifts.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} className="text-center">
                {t("noShiftsScheduled")}
              </TableCell>
            </TableRow>
          ) : (
            sortedShifts.map((shift) => {
              // Calculate shift duration
              const [startHours, startMinutes] = shift.start_time
                .split(":")
                .map(Number);
              const [endHours, endMinutes] = shift.end_time
                .split(":")
                .map(Number);

              const startTotalMinutes = startHours * 60 + startMinutes;
              const endTotalMinutes = endHours * 60 + endMinutes;

              const durationMinutes = endTotalMinutes - startTotalMinutes;
              const durationHours = Math.floor(durationMinutes / 60);
              const remainingMinutes = durationMinutes % 60;

              const duration = `${durationHours}h ${remainingMinutes}m`;

              return (
                <TableRow key={shift.id}>
                  <TableCell className="font-medium">
                    {daysOfWeek[shift.day_of_week]}
                  </TableCell>
                  <TableCell>{formatTime(shift.start_time)}</TableCell>
                  <TableCell>{formatTime(shift.end_time)}</TableCell>
                  <TableCell>{duration}</TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(shift)}
                    >
                      <Edit2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDelete(shift.id)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
};
